import * as React from 'react';
import Tabs from 'antd/lib/tabs';
import Form from 'antd/lib/form';
import Typography from 'antd/lib/typography';
import TextArea from 'antd/lib/input/TextArea';
import Tooltip from 'antd/lib/tooltip';
import Alert from 'antd/lib/alert';
import Space from 'antd/lib/space';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
    AutoApplyMetadataData,
    AutomationType,
    AppStoreType,
    MetadataDisallowedTokens,
    ReleaseNotesDataSource,
} from '../types';
import { getIsAppleAppStore, getMetadataReleaseNotesMaxLength, validatePattern } from '../helpers';
import { automationDisplayNameForAutomationId } from '../helpers/displayHelpers';
import { DefaultMetadataLocale } from '@/types/stepConfig';
import Flex from 'antd/lib/flex';
import styles from './styles/settingsStyles';
import { runwayColors } from './styles/globalStyles';
import { selectAppSettingAutomations } from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';
import { useSelector } from 'react-redux';

interface WhatsNewFormItemProps {
    appStoreType: AppStoreType;
    shouldDisableMetadataEditing?: boolean;
    shouldRequireReleaseNotes?: boolean;
    placeholder?: string;
    name?: string | string[];
}

export const createWhatsNewFormItem = ({
    name = 'whatsNew',
    shouldRequireReleaseNotes = true,
    appStoreType,
    shouldDisableMetadataEditing = false,
    placeholder = "Add information about what's new in this release.",
}: WhatsNewFormItemProps) => {
    const releaseNotesMaxLength = getMetadataReleaseNotesMaxLength(appStoreType);

    return (
        <Form.Item
            name={name}
            rules={[
                {
                    max: releaseNotesMaxLength,
                    message: `Should be less than ${releaseNotesMaxLength} characters`,
                },
                { required: shouldRequireReleaseNotes, message: 'Please enter a release note' },
                {
                    validator: (_, value) => {
                        const pattern = value as string;
                        if (shouldRequireReleaseNotes) {
                            return validatePattern(pattern, 'Release notes', [], MetadataDisallowedTokens);
                        }

                        return Promise.resolve();
                    },
                },
            ]}
        >
            <TextArea
                disabled={shouldDisableMetadataEditing}
                placeholder={placeholder}
                style={{ minWidth: 500, height: 250, position: 'relative' }}
            />
        </Form.Item>
    );
};

const PROMO_TEXT_MAX_LENGTH = 170;

const createPromoTextFormItem = (name: string | string[]) => {
    return (
        <Form.Item
            name={name}
            rules={[
                {
                    max: PROMO_TEXT_MAX_LENGTH,
                    message: `Promotional text must be ${PROMO_TEXT_MAX_LENGTH} characters or less.`,
                },
            ]}
        >
            <TextArea
                placeholder="Add default promotional text."
                style={{ minWidth: 500, height: 120, position: 'relative' }}
            />
        </Form.Item>
    );
};

type DefaultMetadataField = 'whatsNew' | 'promoText';

const SectionTitleWithTooltip: React.FC<{ title: string; tooltip: string }> = ({ title, tooltip }) => (
    <Space style={{ paddingBottom: 8, paddingTop: 8 }}>
        <Typography.Text style={styles.appSettingsSectionTitleStyle}>{title}</Typography.Text>
        <Tooltip title={tooltip}>
            <InfoCircleOutlined style={{ color: runwayColors.primary }} />
        </Tooltip>
    </Space>
);

interface MetadataDefaultFormsProps {
    appId: string;
    stepConfigId: string;
    appStoreType: AppStoreType;
    defaultMetadata: DefaultMetadataLocale[];
}

export const MetadataDefaultFields: React.FC<MetadataDefaultFormsProps> = ({
    appId,
    stepConfigId,
    appStoreType,
    defaultMetadata,
}) => {
    const isAppleAppStore = getIsAppleAppStore(appStoreType);
    const whatsNewTextOrReleaseNotesText = isAppleAppStore ? "'What's new' text" : 'release notes';

    const form = Form.useFormInstance();
    const watchedByLocale: Record<string, { whatsNew?: string; promoText?: string }> | undefined = Form.useWatch(
        'defaultMetadataByLocale',
        form,
    );

    const automationName = automationDisplayNameForAutomationId(AutomationType.AutoApplyMetadata, appStoreType);

    const hasDefaultText = (field: DefaultMetadataField): boolean => {
        if (watchedByLocale) {
            return Object.values(watchedByLocale).some((locale) => !!locale?.[field]?.trim());
        }
        return defaultMetadata.some((locale) => !!locale[field]?.trim());
    };

    const applyMetadataAutomations = useSelector((state: RootState) => {
        const automations = selectAppSettingAutomations(state, appId);
        return (
            automations?.filter(
                (automation) =>
                    automation.stepConfigId === stepConfigId && automation.type === AutomationType.AutoApplyMetadata,
            ) ?? []
        );
    });

    const isConfiguredToUseDefault = (
        getSource: (data?: AutoApplyMetadataData) => ReleaseNotesDataSource | undefined,
    ): boolean =>
        applyMetadataAutomations.some(
            (automation) =>
                automation.enabled &&
                getSource(automation.data as AutoApplyMetadataData | undefined) ===
                    ReleaseNotesDataSource.DefaultReleaseNotes,
        );

    const fieldNeedsBanner = (
        field: DefaultMetadataField,
        getSource: (data?: AutoApplyMetadataData) => ReleaseNotesDataSource | undefined,
    ): boolean => applyMetadataAutomations.length > 0 && hasDefaultText(field) && !isConfiguredToUseDefault(getSource);

    const shouldShowNotConfiguredBanner =
        fieldNeedsBanner('whatsNew', (data) => data?.releaseNotesSource) ||
        (isAppleAppStore && fieldNeedsBanner('promoText', (data) => data?.promoTextSource));

    return (
        <Flex vertical>
            <SectionTitleWithTooltip
                title={`Default ${whatsNewTextOrReleaseNotesText}`}
                tooltip={`You can configure the '${automationName}' automation to use this default text for new releases.`}
            />

            <Tabs destroyInactiveTabPane={false}>
                {defaultMetadata.map((defaultMetadataLocale) => {
                    return (
                        <Tabs.TabPane
                            forceRender
                            key={defaultMetadataLocale.locale}
                            tab={<Typography.Text>{defaultMetadataLocale.displayLocale}</Typography.Text>}
                        >
                            {createWhatsNewFormItem({
                                appStoreType,
                                name: ['defaultMetadataByLocale', defaultMetadataLocale.locale, 'whatsNew'],
                                shouldRequireReleaseNotes: false,
                            })}
                        </Tabs.TabPane>
                    );
                })}
            </Tabs>

            {isAppleAppStore && (
                <>
                    <SectionTitleWithTooltip
                        title="Default promotional text"
                        tooltip={`You can configure the '${automationName}' automation to use this default text for new releases.`}
                    />

                    <Tabs destroyInactiveTabPane={false}>
                        {defaultMetadata.map((defaultMetadataLocale) => {
                            return (
                                <Tabs.TabPane
                                    forceRender
                                    key={defaultMetadataLocale.locale}
                                    tab={<Typography.Text>{defaultMetadataLocale.displayLocale}</Typography.Text>}
                                >
                                    {createPromoTextFormItem([
                                        'defaultMetadataByLocale',
                                        defaultMetadataLocale.locale,
                                        'promoText',
                                    ])}
                                </Tabs.TabPane>
                            );
                        })}
                    </Tabs>
                </>
            )}

            {shouldShowNotConfiguredBanner && (
                <Alert
                    type="info"
                    showIcon={false}
                    style={{ marginTop: 4, marginBottom: 8 }}
                    message={`The '${automationName}' automation is currently disabled or NOT configured to use this default text.`}
                />
            )}
        </Flex>
    );
};
